import { Search, Sticker } from '@/components/ui/icons';
import { useMemo, useState, type ReactNode, type RefObject } from 'react';
import { CUSTOM_EMOJI_EXACT_RE } from '@clanfire/shared';
import { Popover, type Placement } from '@/components/ui/Popover';
import { EMOJI_CATEGORIES, EMOJIS, recentEmoji, rememberEmoji, searchEmoji } from '@/lib/emoji';
import { cn } from '@/lib/utils';
import { useServers } from '@/stores/servers';
import { EmojiGlyph } from './CustomEmoji';

const CATEGORY_ICONS: Record<string, ReactNode> = {
  Recent: '🕘',
  Custom: <Sticker size={18} />,
  Smileys: '😀',
  People: '👋',
  Nature: '🌿',
  Food: '🍕',
  Activities: '🎮',
  Travel: '🚀',
  Objects: '💡',
  Symbols: '❤️',
};

/** One cell in the grid: `value` is what gets picked (a custom emoji's is its <:name:id> token). */
interface Item {
  value: string;
  name: string;
}

function EmojiGrid({ serverId, allowCustom, onSelect }: { serverId?: string | null; allowCustom: boolean; onSelect: (emoji: string) => void }) {
  const [query, setQuery] = useState('');
  const recents = useMemo(() => recentEmoji().filter((e) => allowCustom || !CUSTOM_EMOJI_EXACT_RE.test(e)), [allowCustom]);
  const allCustom = useServers((s) => s.emojis);
  // This server's emoji first, then your other servers'.
  const custom = useMemo<Item[]>(() => {
    if (!allowCustom) return [];
    const list = Object.values(allCustom).sort((a, b) => Number(b.serverId === serverId) - Number(a.serverId === serverId) || a.name.localeCompare(b.name));
    return list.map((e) => ({ value: `<:${e.name}:${e.id}>`, name: e.name }));
  }, [allCustom, serverId, allowCustom]);
  const [category, setCategory] = useState<string>(() => (recents.length ? 'Recent' : custom.length ? 'Custom' : 'Smileys'));
  const [hovered, setHovered] = useState<string | null>(null);

  const list = useMemo<Item[]>(() => {
    const builtIn = (e: { emoji: string; name: string }): Item => ({ value: e.emoji, name: e.name });
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      return [...custom.filter((c) => c.name.toLowerCase().includes(q)), ...searchEmoji(query, 120).map(builtIn)];
    }
    if (category === 'Custom') return custom;
    if (category === 'Recent') {
      return recents.map((e) => {
        const token = CUSTOM_EMOJI_EXACT_RE.exec(e);
        return token ? { value: e, name: token[1]! } : builtIn(EMOJIS.find((x) => x.emoji === e) ?? { emoji: e, name: '' });
      });
    }
    return EMOJIS.filter((e) => e.category === category).map(builtIn);
  }, [query, category, recents, custom]);

  const choose = (emoji: string) => {
    rememberEmoji(emoji);
    onSelect(emoji);
  };

  const tabs = [...(recents.length ? ['Recent'] : []), ...(custom.length ? ['Custom'] : []), ...EMOJI_CATEGORIES];
  return (
    <div className="flex w-[min(344px,calc(100vw-16px))] flex-col">
      <div className="border-b border-line p-2">
        <label className="relative block">
          <Search size={15} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find the perfect emoji"
            aria-label="Search emoji"
            className="h-9 w-full rounded-lg bg-sunken pr-2 pl-8 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
        </label>
      </div>
      {!query && (
        <div role="tablist" aria-label="Emoji categories" className="flex gap-0.5 overflow-x-auto border-b border-line px-1.5 py-1">
          {tabs.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={category === c}
              aria-label={c}
              title={c === 'Custom' ? 'Server emoji' : c}
              onClick={() => setCategory(c)}
              className={cn('flex items-center rounded-md px-1.5 py-1 text-lg transition-colors', category === c ? 'bg-active' : 'opacity-60 hover:bg-hover hover:opacity-100')}
            >
              {CATEGORY_ICONS[c]}
            </button>
          ))}
        </div>
      )}
      <div
        className="grid h-[260px] grid-cols-8 content-start gap-0.5 overflow-y-auto p-2"
        role="listbox"
        aria-label="Emoji"
        onKeyDown={(ev) => {
          // Arrow keys move around the 8-column grid.
          const step = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: 8, ArrowUp: -8 } as Record<string, number>)[ev.key];
          if (!step) return;
          const buttons = [...ev.currentTarget.querySelectorAll<HTMLButtonElement>('button')];
          const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
          if (i < 0) return;
          ev.preventDefault();
          buttons[Math.min(buttons.length - 1, Math.max(0, i + step))]?.focus();
        }}
      >
        {list.map((e) => (
          <button
            key={e.value + e.name}
            type="button"
            role="option"
            aria-selected={false}
            aria-label={e.name || e.value}
            onClick={() => choose(e.value)}
            onMouseEnter={() => setHovered(e.name)}
            onFocus={() => setHovered(e.name)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[22px] transition-transform hover:scale-110 hover:bg-hover focus:bg-hover"
          >
            <EmojiGlyph emoji={e.value} className="h-7! w-7!" />
          </button>
        ))}
        {list.length === 0 && (
          <p className="col-span-8 py-8 text-center text-sm text-fg-muted">{query ? `No emoji match “${query}”.` : 'No emoji here yet.'}</p>
        )}
      </div>
      <div className="h-8 truncate border-t border-line px-3 py-1.5 text-xs text-fg-muted">{hovered ? `:${hovered}:` : 'Pick an emoji'}</div>
    </div>
  );
}

export function EmojiPicker({
  open,
  onClose,
  anchor,
  onSelect,
  placement = 'top-end',
  serverId,
  allowCustom = true,
}: {
  open: boolean;
  onClose: () => void;
  anchor: RefObject<HTMLElement | null>;
  /** A Unicode emoji, or a custom emoji's <:name:id> token. */
  onSelect: (emoji: string) => void;
  placement?: Placement;
  /** Whose custom emoji come first. */
  serverId?: string | null;
  /** Offer custom server emoji (not for things that need a Unicode emoji). */
  allowCustom?: boolean;
}) {
  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement={placement} label="Emoji picker">
      <EmojiGrid
        serverId={serverId}
        allowCustom={allowCustom}
        onSelect={(e) => {
          onSelect(e);
          onClose();
        }}
      />
    </Popover>
  );
}
